// src/context/BalanceContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import app from '../pages/firebase'; // Import the already initialized Firebase app

export const BalanceContext = createContext();

export const BalanceProvider = ({ children }) => {
  const { publicKey } = useWallet();
  const [solBalance, setSolBalance] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rewardLoading, setRewardLoading] = useState(false);
  const EMCII_MINT = 'Gc3eJ7fhAaJR2YsxyNs9nCb4HydBiLryKGxghW613sYK';

  // Memoize fetchBalances with useCallback to ensure dependencies remain stable
  const fetchBalances = useCallback(async () => {
    if (!publicKey) return;
    setLoading(true);
    const walletId = publicKey.toBase58();
    try {
      const tokenRes = await fetch(`/balance?wallet_id=${walletId}&token_id=${EMCII_MINT}`);
      if (tokenRes.ok) {
        const tokenData = await tokenRes.json();
        setTokenBalance(tokenData.balance);
      } else {
        throw new Error(`Error fetching EMCII balance: ${tokenRes.status}`);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [publicKey, EMCII_MINT]);

  // Function to fetch the current user's reward from Firestore
  const fetchReward = useCallback(async () => {
    setRewardLoading(true);
    try {
      const auth = getAuth(app);
      if (!auth.currentUser) {
        console.error('User is null');
        return;
      }
      const firestore = getFirestore(app);
      const uid = auth.currentUser.uid;
      const userDocRef = doc(firestore, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        setReward(userData.reward);
      } else {
        console.error('User document not found');
      }
    } catch (err) {
      console.error('Error fetching reward:', err);
    } finally {
      setRewardLoading(false);
    }
  }, []);

  // Fetch balances when publicKey changes
  useEffect(() => {
    if (publicKey) {
      // Errors are handled by the caller, so no catch here
      fetchBalances().catch(() => {});
    }
  }, [publicKey, fetchBalances]);

  // Fetch reward when there is a change in Firebase Auth user information
  useEffect(() => {
    fetchReward();
  }, [fetchReward]);

  return (
    <BalanceContext.Provider
      value={{
        solBalance,
        tokenBalance,
        reward,
        fetchBalances,
        fetchReward,
        loading,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error("useBalance must be used within a BalanceProvider");
  }
  return context;
};
